import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Box,
    VStack,
    HStack,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    IconButton,
    Select,
    Avatar,
    Badge,
    Divider,
    Spinner,
    Alert,
    AlertIcon,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Checkbox,
    CheckboxGroup,
    useToast,
    useColorMode,
} from "@chakra-ui/react";
import { Tooltip } from "./Tip";
import { SearchIcon } from '@chakra-ui/icons';
import { FiUserPlus, FiTrash2, FiShare2, FiUsers } from 'react-icons/fi';
import { getColor } from '../../../brandConfig';
import {
  GET_CALENDAR_SHARES,
  CALENDAR_SEARCH_PEOPLE,
  SHARE_CALENDAR_WITH_CLIENTS,
  UNSHARE_CALENDAR,
  GET_CALENDAR_AUDIENCE_OPTIONS,
  GET_CALENDAR_AUDIENCE_SHARES,
  SHARE_CALENDAR_WITH_AUDIENCES,
  PREVIEW_CALENDAR_AUDIENCES,
  UNSHARE_CALENDAR_AUDIENCE,
} from '../queries';

/**
 * ShareCalendarModal — share a calendar with people or whole groups.
 *
 * People tab: type a first name, last name or nickname to find someone
 * (employees show with their roles; terminated staff never appear), choose
 * per person: Can view / Can view & edit.
 *
 * Groups tab: share with everyone holding an employee ROLE (active staff,
 * live rule — new hires with the role gain access automatically) or a client
 * TAG. A preview of exactly who that reaches RIGHT NOW always shows before
 * the share button unlocks.
 *
 * Everyone reached gets an in-app notification (backend-driven).
 * Baby-boomer standard: plain-English tooltips on every control.
 */

interface PendingShare {
  clientId: string;
  name: string;
  email?: string;
  permission: 'VIEW' | 'EDIT';
  isEmployee: boolean;
  roleNames?: string[];
}

interface ShareCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendarId: string;
  calendarName: string;
  /** Called after a successful share/unshare so parents can refetch */
  onChanged?: () => void;
}

/** "Katherine (Katie) Mansfield" — nickname in brackets when it differs from the first name. */
export const formatPersonName = (p: {
  fName?: string | null;
  lName?: string | null;
  preferredName?: string | null;
  email?: string | null;
}): string => {
  const first = (p.fName || '').trim();
  const last = (p.lName || '').trim();
  const nick = (p.preferredName || '').trim();
  const showNick = nick && nick.toLowerCase() !== first.toLowerCase();
  const name = `${first}${showNick ? ` (${nick})` : ''} ${last}`.trim();
  return name || p.email || 'Unnamed';
};

const PersonBadges: React.FC<{ isEmployee: boolean; roleNames?: string[] | null }> = ({
  isEmployee,
  roleNames,
}) => (
  <HStack spacing={1} flexWrap="wrap" rowGap={1}>
    {isEmployee ? (
      <Tooltip label="This person has an employee record on this site" hasArrow openDelay={300}>
        <Badge colorScheme="blue" cursor="help">EMPLOYEE</Badge>
      </Tooltip>
    ) : (
      <Tooltip label="This person is a client record (no employee record)" hasArrow openDelay={300}>
        <Badge colorScheme="gray" cursor="help">CLIENT</Badge>
      </Tooltip>
    )}
    {(roleNames || []).slice(0, 2).map((r) => (
      <Badge key={r} colorScheme="purple" variant="subtle">{r}</Badge>
    ))}
    {(roleNames?.length || 0) > 2 && (
      <Tooltip label={roleNames!.slice(2).join(', ')} hasArrow openDelay={300}>
        <Badge colorScheme="purple" variant="outline" cursor="help">+{roleNames!.length - 2}</Badge>
      </Tooltip>
    )}
  </HStack>
);

const ShareCalendarModal: React.FC<ShareCalendarModalProps> = ({
  isOpen,
  onClose,
  calendarId,
  calendarName,
  onChanged,
}) => {
  const toast = useToast();
  const { colorMode } = useColorMode();

  const cardGradientBg = getColor('background.cardGradient', colorMode);
  const cardBorder = getColor('border.darkCard', colorMode);
  const textPrimary = getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode);
  const textSecondary = getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode);
  const primaryColor = getColor('primary', colorMode);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pending, setPending] = useState<PendingShare[]>([]);

  // Group-share state — several roles/tags can be picked at once
  const [groupKind, setGroupKind] = useState<'ROLE' | 'TAG'>('ROLE');
  const [groupValues, setGroupValues] = useState<string[]>([]);
  const [groupPermission, setGroupPermission] = useState<'VIEW' | 'EDIT'>('VIEW');

  // Debounce the typeahead so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const {
    data: sharesData,
    loading: sharesLoading,
    refetch: refetchShares,
  } = useQuery(GET_CALENDAR_SHARES, {
    variables: { calendarId },
    skip: !isOpen || !calendarId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: audienceSharesData, refetch: refetchAudienceShares } = useQuery(
    GET_CALENDAR_AUDIENCE_SHARES,
    {
      variables: { calendarId },
      skip: !isOpen || !calendarId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: optionsData } = useQuery(GET_CALENDAR_AUDIENCE_OPTIONS, {
    skip: !isOpen,
  });

  const [runSearch, { data: searchData, loading: searchLoading }] = useLazyQuery(
    CALENDAR_SEARCH_PEOPLE,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      runSearch({ variables: { search: debouncedSearch } });
    }
  }, [debouncedSearch, runSearch]);

  // Mandatory preview: fires whenever the group selection changes (union, deduped)
  const [runPreview, { data: previewData, loading: previewLoading }] = useLazyQuery(
    PREVIEW_CALENDAR_AUDIENCES,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (groupValues.length) {
      runPreview({
        variables: { targets: groupValues.map((value) => ({ kind: groupKind, value })) },
      });
    }
  }, [groupKind, groupValues, runPreview]);

  const refetchAll = () => {
    refetchShares();
    refetchAudienceShares();
    onChanged?.();
  };

  const [shareCalendarWithClients, { loading: sharing }] = useMutation(SHARE_CALENDAR_WITH_CLIENTS, {
    onCompleted: () => {
      toast({
        title: 'Calendar shared',
        description: 'Each person now has access and has been sent a notification.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setPending([]);
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not share the calendar', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  const [unshareCalendar, { loading: unsharing }] = useMutation(UNSHARE_CALENDAR, {
    onCompleted: () => {
      toast({ title: 'Access removed', status: 'success', duration: 3000, isClosable: true });
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not remove access', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  const [shareWithAudiences, { loading: audienceSharing }] = useMutation(SHARE_CALENDAR_WITH_AUDIENCES, {
    onCompleted: () => {
      toast({
        title: 'Group share added',
        description: 'Everyone in the groups has been notified (once each). People who join a group later gain access automatically.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setGroupValues([]);
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not add the group share', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  const [unshareAudience, { loading: audienceUnsharing }] = useMutation(UNSHARE_CALENDAR_AUDIENCE, {
    onCompleted: () => {
      toast({ title: 'Group share removed', description: 'Everyone in the group has been notified.', status: 'success', duration: 4000, isClosable: true });
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not remove the group share', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  // useMemo so the searchResults memo below doesn't see a fresh [] each render
  const existingShares: any[] = useMemo(() => sharesData?.calendarShares || [], [sharesData]);
  const audienceRules: any[] = audienceSharesData?.calendarAudienceShares || [];
  const previewMembers: any[] = previewData?.previewCalendarAudiences || [];
  const roleOptions: any[] = optionsData?.calendarAudienceOptions?.roles || [];
  const tagOptions: string[] = optionsData?.calendarAudienceOptions?.tags || [];

  // Hide people already shared-with (or already queued) from the search results
  const searchResults = useMemo(() => {
    const taken = new Set([
      ...existingShares.map((s) => s.clientId),
      ...pending.map((p) => p.clientId),
    ]);
    return (searchData?.calendarSearchPeople || []).filter((c: any) => !taken.has(c.clientId));
  }, [searchData, existingShares, pending]);

  const addPending = (person: any) => {
    setPending((prev) => [
      ...prev,
      {
        clientId: person.clientId,
        name: formatPersonName(person),
        email: person.email || undefined,
        permission: 'VIEW',
        isEmployee: person.isEmployee,
        roleNames: person.roleNames || undefined,
      },
    ]);
    setSearchTerm('');
    setDebouncedSearch('');
  };

  const setPendingPermission = (clientId: string, permission: 'VIEW' | 'EDIT') => {
    setPending((prev) => prev.map((p) => (p.clientId === clientId ? { ...p, permission } : p)));
  };

  const removePending = (clientId: string) => {
    setPending((prev) => prev.filter((p) => p.clientId !== clientId));
  };

  const handleShare = () => {
    if (!pending.length) return;
    shareCalendarWithClients({
      variables: {
        calendarId,
        shares: pending.map((p) => ({ clientId: p.clientId, permission: p.permission })),
      },
    });
  };

  const handleUpdateExisting = (clientId: string, permission: 'VIEW' | 'EDIT') => {
    shareCalendarWithClients({
      variables: { calendarId, shares: [{ clientId, permission }] },
    });
  };

  const handleGroupShare = () => {
    if (!groupValues.length) return;
    shareWithAudiences({
      variables: {
        calendarId,
        targets: groupValues.map((value) => ({ kind: groupKind, value })),
        permission: groupPermission,
      },
    });
  };

  const permissionBadge = (permission: string) =>
    permission === 'EDIT' || permission === 'ADMIN' ? (
      <Badge colorScheme="purple">Can view & edit</Badge>
    ) : (
      <Badge colorScheme="blue">Can view</Badge>
    );

  const groupValueLabels = groupValues
    .map((v) => (groupKind === 'ROLE' ? roleOptions.find((r) => r.id === v)?.name || v : v))
    .join(', ');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        bg={cardGradientBg}
        border="1px solid"
        borderColor={cardBorder}
        margin={{ base: 0, md: 'auto' }}
        borderRadius={{ base: 0, md: 'md' }}
      >
        <ModalHeader color={textPrimary} fontSize={{ base: 'lg', md: 'xl' }}>
          <HStack spacing={3}>
            <FiShare2 />
            <Text>Share “{calendarName}”</Text>
          </HStack>
          <Text fontSize="md" fontWeight="normal" color={textSecondary} mt={1}>
            Give people — or whole groups — access to see or help manage this calendar.
          </Text>
        </ModalHeader>
        <Tooltip label="Close this window without making more changes" hasArrow openDelay={300}>
          <ModalCloseButton color={textPrimary} />
        </Tooltip>

        <ModalBody pb={4}>
          <VStack spacing={5} align="stretch">
            <Tabs colorScheme="blue" variant="enclosed">
              <TabList>
                <Tooltip label="Share with one person at a time — search them by name" hasArrow openDelay={300}>
                  <Tab color={textPrimary} fontSize="md"><HStack spacing={2}><FiUserPlus /><Text>A person</Text></HStack></Tab>
                </Tooltip>
                <Tooltip label="Share with everyone holding a role (e.g. all active Registered Nurses) or a client tag — updates itself as people join or leave the group" hasArrow openDelay={300}>
                  <Tab color={textPrimary} fontSize="md"><HStack spacing={2}><FiUsers /><Text>A group</Text></HStack></Tab>
                </Tooltip>
              </TabList>

              <TabPanels>
                {/* ============ PEOPLE TAB ============ */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Tooltip
                      label="Start typing a first name, last name or nickname — matching people appear below. People who no longer work here won't show up."
                      hasArrow
                      openDelay={300}
                    >
                      <InputGroup size="lg">
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color={textSecondary} />
                        </InputLeftElement>
                        <Input
                          placeholder="Type a name or nickname to search…"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          color={textPrimary}
                          bg="whiteAlpha.100"
                          borderColor={cardBorder}
                        />
                      </InputGroup>
                    </Tooltip>

                    {searchLoading && debouncedSearch.length >= 2 && (
                      <HStack>
                        <Spinner size="sm" color={primaryColor} />
                        <Text fontSize="md" color={textSecondary}>Searching…</Text>
                      </HStack>
                    )}

                    {debouncedSearch.length >= 2 && !searchLoading && (
                      <VStack
                        align="stretch"
                        spacing={0}
                        maxH="220px"
                        overflowY="auto"
                        border="1px solid"
                        borderColor={cardBorder}
                        borderRadius="md"
                      >
                        {searchResults.length === 0 && (
                          <Text p={3} fontSize="md" color={textSecondary}>
                            No one found matching “{debouncedSearch}”. Check the spelling, or they may
                            already have access below.
                          </Text>
                        )}
                        {searchResults.map((person: any) => (
                          <Tooltip
                            key={person.clientId}
                            label={`Add ${formatPersonName(person)} to the share list`}
                            hasArrow
                            openDelay={300}
                          >
                            <HStack
                              p={3}
                              minH="48px"
                              spacing={3}
                              cursor="pointer"
                              _hover={{ bg: 'whiteAlpha.200' }}
                              onClick={() => addPending(person)}
                            >
                              <Avatar size="sm" name={formatPersonName(person)} />
                              <Box flex="1" minW={0}>
                                <Text color={textPrimary} fontSize="md" fontWeight="medium" noOfLines={1}>
                                  {formatPersonName(person)}
                                </Text>
                                {person.email && (
                                  <Text color={textSecondary} fontSize="sm" noOfLines={1}>
                                    {person.email}
                                  </Text>
                                )}
                              </Box>
                              <PersonBadges isEmployee={person.isEmployee} roleNames={person.roleNames} />
                              <FiUserPlus color={primaryColor as string} />
                            </HStack>
                          </Tooltip>
                        ))}
                      </VStack>
                    )}

                    {/* Pending (about to be shared) */}
                    {pending.length > 0 && (
                      <Box>
                        <Text color={textPrimary} fontSize="md" fontWeight="semibold" mb={2}>
                          Ready to share with
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {pending.map((p) => (
                            <HStack
                              key={p.clientId}
                              p={3}
                              minH="48px"
                              bg="whiteAlpha.50"
                              border="1px solid"
                              borderColor={cardBorder}
                              borderRadius="md"
                              spacing={3}
                            >
                              <Avatar size="sm" name={p.name} />
                              <Box flex="1" minW={0}>
                                <Text color={textPrimary} fontSize="md" fontWeight="medium" noOfLines={1}>
                                  {p.name}
                                </Text>
                                <HStack spacing={2}>
                                  <PersonBadges isEmployee={p.isEmployee} roleNames={p.roleNames} />
                                </HStack>
                              </Box>
                              <Tooltip
                                label={'"Can view" lets them see events. "Can view & edit" also lets them add and change events.'}
                                hasArrow
                                openDelay={300}
                              >
                                <Select
                                  value={p.permission}
                                  onChange={(e) => setPendingPermission(p.clientId, e.target.value as 'VIEW' | 'EDIT')}
                                  size="md"
                                  w="170px"
                                  color={textPrimary}
                                  bg="whiteAlpha.100"
                                  borderColor={cardBorder}
                                >
                                  <option value="VIEW">Can view</option>
                                  <option value="EDIT">Can view & edit</option>
                                </Select>
                              </Tooltip>
                              <Tooltip label={`Remove ${p.name} from this list before sharing`} hasArrow openDelay={300}>
                                <IconButton
                                  aria-label={`Remove ${p.name} from the share list`}
                                  icon={<FiTrash2 />}
                                  size="md"
                                  variant="ghost"
                                  color="red.400"
                                  onClick={() => removePending(p.clientId)}
                                />
                              </Tooltip>
                            </HStack>
                          ))}
                        </VStack>
                        <Tooltip shouldWrapChildren
                          label={`Give ${pending.length} ${pending.length === 1 ? 'person' : 'people'} access — each will get a notification`}
                          hasArrow
                          openDelay={300}
                        >
                          <Button
                            mt={3}
                            leftIcon={<FiShare2 />}
                            colorScheme="blue"
                            size="lg"
                            onClick={handleShare}
                            isLoading={sharing}
                          >
                            Share with {pending.length} {pending.length === 1 ? 'person' : 'people'}
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* ============ GROUPS TAB ============ */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Alert status="info" borderRadius="md" fontSize="md">
                      <AlertIcon />
                      A group share stays up to date by itself: anyone who joins the role or tag later
                      gets access automatically, and anyone who leaves loses it.
                    </Alert>

                    <HStack spacing={3} flexWrap="wrap" rowGap={2} align="start">
                      <Tooltip label="Choose whether to share by employee role or by client tag" hasArrow openDelay={300}>
                        <Select
                          value={groupKind}
                          onChange={(e) => { setGroupKind(e.target.value as 'ROLE' | 'TAG'); setGroupValues([]); }}
                          size="lg"
                          w={{ base: 'full', md: '230px' }}
                          color={textPrimary}
                          bg="whiteAlpha.100"
                          borderColor={cardBorder}
                        >
                          <option value="ROLE">Employees with a role</option>
                          <option value="TAG">Clients with a tag</option>
                        </Select>
                      </Tooltip>

                      <Tooltip
                        label={'"Can view" lets the groups see events. "Can view & edit" also lets them add and change events.'}
                        hasArrow
                        openDelay={300}
                      >
                        <Select
                          value={groupPermission}
                          onChange={(e) => setGroupPermission(e.target.value as 'VIEW' | 'EDIT')}
                          size="lg"
                          w={{ base: 'full', md: '190px' }}
                          color={textPrimary}
                          bg="whiteAlpha.100"
                          borderColor={cardBorder}
                        >
                          <option value="VIEW">Can view</option>
                          <option value="EDIT">Can view & edit</option>
                        </Select>
                      </Tooltip>
                    </HStack>

                    {/* Multi-select: tick as many roles/tags as you like */}
                    <Box border="1px solid" borderColor={cardBorder} borderRadius="md" p={3}>
                      <Text color={textPrimary} fontSize="md" fontWeight="semibold" mb={2}>
                        {groupKind === 'ROLE'
                          ? 'Tick one or more roles — only active employees holding them are included'
                          : 'Tick one or more tags — every client carrying them is included'}
                      </Text>
                      <CheckboxGroup
                        value={groupValues}
                        onChange={(vals) => setGroupValues(vals as string[])}
                      >
                        <VStack align="stretch" spacing={1} maxH="180px" overflowY="auto">
                          {(groupKind === 'ROLE' ? roleOptions.map((r) => ({ v: r.id, label: r.name })) : tagOptions.map((t) => ({ v: t, label: t }))).map(({ v, label }) => (
                            <Tooltip key={v} label={`Include everyone ${groupKind === 'ROLE' ? `with the ${label} role` : `tagged ${label}`} in this share`} hasArrow openDelay={300}>
                              <Box>
                                <Checkbox value={v} size="lg" colorScheme="green" color={textPrimary}>
                                  <Text fontSize="md" color={textPrimary}>{label}</Text>
                                </Checkbox>
                              </Box>
                            </Tooltip>
                          ))}
                          {(groupKind === 'ROLE' ? roleOptions : tagOptions).length === 0 && (
                            <Text fontSize="md" color={textSecondary}>
                              {groupKind === 'ROLE' ? 'No roles are set up on this site yet.' : 'No client tags exist on this site yet.'}
                            </Text>
                          )}
                        </VStack>
                      </CheckboxGroup>
                    </Box>

                    {/* Mandatory preview */}
                    {groupValues.length > 0 && (
                      <Box border="1px solid" borderColor={cardBorder} borderRadius="md" p={3}>
                        {previewLoading ? (
                          <HStack>
                            <Spinner size="sm" color={primaryColor} />
                            <Text fontSize="md" color={textSecondary}>Checking who this reaches…</Text>
                          </HStack>
                        ) : (
                          <>
                            <Text color={textPrimary} fontSize="md" fontWeight="semibold" mb={2}>
                              This will share with {previewMembers.length}{' '}
                              {previewMembers.length === 1 ? 'person' : 'people'} right now:
                            </Text>
                            {previewMembers.length === 0 && (
                              <Text fontSize="md" color={textSecondary}>
                                No one currently matches — the share would only take effect when someone
                                joins this {groupKind === 'ROLE' ? 'role' : 'tag'}.
                              </Text>
                            )}
                            <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
                              {previewMembers.map((m: any) => (
                                <HStack key={m.clientId} p={2} spacing={3}>
                                  <Avatar size="xs" name={formatPersonName(m)} />
                                  <Text color={textPrimary} fontSize="md" flex="1" noOfLines={1}>
                                    {formatPersonName(m)}
                                  </Text>
                                  <PersonBadges isEmployee={m.isEmployee} roleNames={m.roleNames} />
                                </HStack>
                              ))}
                            </VStack>
                            <Tooltip shouldWrapChildren
                              label={`Share this calendar with ${groupKind === 'ROLE' ? `everyone holding: ${groupValueLabels}` : `everyone tagged: ${groupValueLabels}`} — the ${previewMembers.length} ${previewMembers.length === 1 ? 'person' : 'people'} above will each be notified once`}
                              hasArrow
                              openDelay={300}
                            >
                              <Button
                                mt={3}
                                leftIcon={<FiUsers />}
                                colorScheme="green"
                                size="lg"
                                onClick={handleGroupShare}
                                isLoading={audienceSharing}
                                isDisabled={previewMembers.length === 0 && groupKind === 'TAG'}
                              >
                                Share with {groupValues.length === 1 ? 'this group' : `these ${groupValues.length} groups`}
                              </Button>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>

            <Divider borderColor={cardBorder} />

            {/* Existing access */}
            <Box>
              <Text color={textPrimary} fontSize="md" fontWeight="semibold" mb={2}>
                Who already has access
              </Text>
              {sharesLoading && !existingShares.length && !audienceRules.length ? (
                <HStack>
                  <Spinner size="sm" color={primaryColor} />
                  <Text fontSize="md" color={textSecondary}>Loading…</Text>
                </HStack>
              ) : existingShares.length === 0 && audienceRules.length === 0 ? (
                <Alert status="info" borderRadius="md" fontSize="md">
                  <AlertIcon />
                  This calendar hasn’t been shared with anyone yet. Only you (and site admins) can see it.
                </Alert>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {/* Group rules first */}
                  {audienceRules.map((rule) => (
                    <HStack
                      key={`${rule.kind}:${rule.value}`}
                      p={3}
                      minH="48px"
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={primaryColor}
                      borderRadius="md"
                      spacing={3}
                    >
                      <Box color={primaryColor}><FiUsers size={20} /></Box>
                      <Box flex="1" minW={0}>
                        <Text color={textPrimary} fontSize="md" fontWeight="medium" noOfLines={1}>
                          {rule.kind === 'ROLE' ? `Everyone with the role “${rule.label || rule.value}”` : `Everyone tagged “${rule.label || rule.value}”`}
                        </Text>
                        <HStack spacing={2}>
                          {permissionBadge(rule.permission)}
                          <Tooltip label="How many people this group rule reaches right now — it updates automatically as people join or leave" hasArrow openDelay={300}>
                            <Badge colorScheme="green" variant="subtle" cursor="help">
                              currently {rule.memberCount} {rule.memberCount === 1 ? 'person' : 'people'}
                            </Badge>
                          </Tooltip>
                          {rule.sharedByName && (
                            <Text color={textSecondary} fontSize="sm" noOfLines={1}>
                              shared by {rule.sharedByName}
                            </Text>
                          )}
                        </HStack>
                      </Box>
                      <Tooltip shouldWrapChildren label="Stop sharing with this group — everyone currently in it will be notified" hasArrow openDelay={300}>
                        <IconButton
                          aria-label={`Remove the ${rule.label || rule.value} group share`}
                          icon={<FiTrash2 />}
                          size="md"
                          variant="ghost"
                          color="red.400"
                          isLoading={audienceUnsharing}
                          onClick={() => unshareAudience({ variables: { calendarId, kind: rule.kind, value: rule.value } })}
                        />
                      </Tooltip>
                    </HStack>
                  ))}

                  {/* Individual shares */}
                  {existingShares.map((s) => (
                    <HStack
                      key={s.clientId}
                      p={3}
                      minH="48px"
                      bg="whiteAlpha.50"
                      border="1px solid"
                      borderColor={cardBorder}
                      borderRadius="md"
                      spacing={3}
                    >
                      <Avatar size="sm" name={s.clientName || 'Unknown'} />
                      <Box flex="1" minW={0}>
                        <Text color={textPrimary} fontSize="md" fontWeight="medium" noOfLines={1}>
                          {formatPersonName({ fName: s.clientName, lName: '', preferredName: s.preferredName, email: s.clientEmail })}
                        </Text>
                        <HStack spacing={2} flexWrap="wrap" rowGap={1}>
                          {permissionBadge(s.permission)}
                          <PersonBadges isEmployee={s.isEmployee} roleNames={s.roleNames} />
                          {s.sharedByName && (
                            <Text color={textSecondary} fontSize="sm" noOfLines={1}>
                              shared by {s.sharedByName}
                            </Text>
                          )}
                        </HStack>
                      </Box>
                      <Tooltip shouldWrapChildren label="Change what this person can do on the calendar" hasArrow openDelay={300}>
                        <Select
                          value={s.permission === 'EDIT' || s.permission === 'ADMIN' ? 'EDIT' : 'VIEW'}
                          onChange={(e) => handleUpdateExisting(s.clientId, e.target.value as 'VIEW' | 'EDIT')}
                          size="md"
                          w="170px"
                          color={textPrimary}
                          bg="whiteAlpha.100"
                          borderColor={cardBorder}
                          isDisabled={sharing}
                        >
                          <option value="VIEW">Can view</option>
                          <option value="EDIT">Can view & edit</option>
                        </Select>
                      </Tooltip>
                      <Tooltip shouldWrapChildren
                        label={`Stop sharing this calendar with ${s.clientName || 'this person'} — they'll be notified`}
                        hasArrow
                        openDelay={300}
                      >
                        <IconButton
                          aria-label={`Remove access for ${s.clientName || 'this person'}`}
                          icon={<FiTrash2 />}
                          size="md"
                          variant="ghost"
                          color="red.400"
                          isLoading={unsharing}
                          onClick={() => unshareCalendar({ variables: { calendarId, clientId: s.clientId } })}
                        />
                      </Tooltip>
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Tooltip label="Close this window — any shares you've already made are saved" hasArrow openDelay={300}>
            <Button variant="ghost" size="lg" onClick={onClose} color={textSecondary}>
              Done
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareCalendarModal;
